import React, { useState } from 'react'
import styled from 'styled-components'
import { Close } from 'sutra/components/Icons'
import { brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'

export interface MultiInputProps {
  value: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  // Characters that split typed/pasted text into separate chips as you type
  // (not just on paste) — matches antd Select's `tokenSeparators` feel.
  tokenSeparators?: string[]
}

const DEFAULT_TOKEN_SEPARATORS = [',', '\n']

const Container = styled.div<{ $focused: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-height: 30px;
  padding: 3px 6px;
  font-family: 'Poppins', system-ui, sans-serif;
  background: ${brand.white};
  border: 1px solid
    ${({ $focused }) => ($focused ? brand.stillNight[800] : gray[300])};
  border-radius: ${radii.sm}px;
  transition: border-color ${motion.duration.base}ms ${motion.ease};
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  font-size: 12px;
  color: ${brand.stillNight[800]};
  background: ${gray[100]};
  border-radius: ${radii.xs}px;
`

const ChipRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: ${gray[500]};
  cursor: pointer;
  &:hover {
    color: ${brand.stillNight[800]};
  }
`

const TextInput = styled.input`
  flex: 1;
  min-width: 60px;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 13px;
  color: ${brand.stillNight[800]};
  background: transparent;
  &::placeholder {
    color: #a4a7ae;
  }
`

// A free-text chip input — type, then Enter or a separator character (comma
// / newline by default) turns the typed text into a chip. No dropdown, no
// suggestions; that's `Select`'s job. Splitting/committing raw text is all
// this does — callers that need trim/dedupe/quote-stripping run the emitted
// array through their own cleanup (this mirrors how the antd tags-Select
// usage it replaces worked: the caller re-parsed on every change).
export const MultiInput: React.FC<MultiInputProps> = ({
  value,
  onChange,
  placeholder,
  tokenSeparators = DEFAULT_TOKEN_SEPARATORS,
}) => {
  const [draftText, setDraftText] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  const separatorPattern = new RegExp(
    `[${tokenSeparators.map((s) => (s === '\n' ? '\\n' : s)).join('')}]`,
  )

  const handleTextChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const raw = event.target.value
    if (!separatorPattern.test(raw)) {
      setDraftText(raw)
      return
    }
    const segments = raw.split(separatorPattern)
    const trailing = segments.pop() ?? ''
    const newChips = segments.map((s) => s.trim()).filter((s) => s.length > 0)
    if (newChips.length > 0) {
      onChange([...value, ...newChips])
    }
    setDraftText(trailing)
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      const trimmed = draftText.trim()
      if (trimmed.length > 0) {
        onChange([...value, trimmed])
        setDraftText('')
      }
    } else if (
      event.key === 'Backspace' &&
      draftText.length === 0 &&
      value.length > 0
    ) {
      onChange(value.slice(0, -1))
    }
  }

  const removeChip = (index: number): void => {
    onChange(value.filter((_, i) => i !== index))
  }

  return (
    <Container $focused={isFocused}>
      {value.map((chip, index) => (
        <Chip key={`${chip}-${index}`}>
          {chip}
          <ChipRemoveButton
            type="button"
            aria-label={`Remove ${chip}`}
            onClick={() => removeChip(index)}
          >
            <Close width={10} height={10} />
          </ChipRemoveButton>
        </Chip>
      ))}
      <TextInput
        value={draftText}
        placeholder={value.length === 0 ? placeholder : undefined}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
    </Container>
  )
}

MultiInput.displayName = 'MultiInput'
